import { ArrowOutwardRounded, Menu as MenuIcon } from "@mui/icons-material"
import {
  AppBar,
  Box,
  Button,
  ButtonProps,
  Container,
  Drawer,
  Grid,
  IconButton,
  Link as MuiLink,
  List,
  ListItem,
  Stack,
  Toolbar,
  useScrollTrigger,
} from "@mui/material"
import React, { useState } from "react"
import { Logo } from "src/components/Header/Logo"
import { SPRING_CONFIGS } from "src/utils/utils"

import { HeroSection } from "./HeroSection"
import { ShimmerButton } from "./ShimmerButton"
import { StaggeredList } from "./StaggeredList"
import { ThemeMode } from "./ThemeMode"

type NavButtonProps = {
  children: React.ReactNode
  href?: string
  to?: string
} & ButtonProps

const NavButton = ({ sx, to, href, children, ...props }: NavButtonProps) => {
  const currentPath = typeof window !== "undefined" ? window.location.pathname : ""
  const isActive = to ? currentPath === to : false
  const isExternal = href?.includes("http")

  return (
    <Button
      component={MuiLink}
      href={isExternal ? href : to || href}
      color="inherit"
      target={isExternal ? "_blank" : undefined}
      sx={{
        "&:hover": {
          bgcolor: isActive
            ? "rgba(var(--mui-palette-primary-mainChannel) / 0.15)"
            : "rgba(var(--mui-palette-primary-mainChannel) / 0.05)",
        },
        bgcolor: isActive ? "rgba(var(--mui-palette-primary-mainChannel) / 0.1)" : "transparent",
        color: isActive ? "primary.main" : "inherit",
        gap: 0.5,
        marginX: 0.5,
        paddingX: 2,
        ...sx,
      }}
      {...props}
    >
      {children}
      {isExternal && <ArrowOutwardRounded sx={{ fontSize: "1rem" }} />}
    </Button>
  )
}

export default function Header() {
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 50,
  })
  const [mobileOpen, setMobileOpen] = useState(false)

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen)
  }

  const navItems = [
    { label: "Apps", to: "/apps" },
    { label: "Pricing", to: "/pricing" },
    { href: "https://docs.privatefolio.app", label: "Docs" },
    { href: "https://paragraph.xyz/@privatefolio", label: "Blog" },
  ]

  const drawer = (
    <Box onClick={handleDrawerToggle}>
      <List>
        <ListItem disablePadding>
          <Button
            component={MuiLink}
            href="/"
            sx={{ justifyContent: "flex-start", paddingX: 4, paddingY: 2 }}
            fullWidth
          >
            <Logo />
          </Button>
        </ListItem>
        {navItems.map((item) => (
          <ListItem key={item.label} disablePadding>
            <NavButton
              {...item}
              fullWidth
              sx={{ justifyContent: "flex-start", paddingX: 4, paddingY: 2 }}
            >
              {item.label}
            </NavButton>
          </ListItem>
        ))}
        <ListItem disablePadding>
          <ThemeMode
            sx={{
              borderRadius: 1,
              display: { sm: "none", xs: "inline-flex" },
              justifyContent: "flex-start",
              marginX: 0.5,
              paddingX: 4,
              paddingY: 2,
              width: "calc(100% - 8px)",
            }}
          />
        </ListItem>
      </List>
    </Box>
  )

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          backdropFilter: "none !important",
          background: "none !important",
          border: "none",
          color: "var(--mui-palette-primary-main) !important",
        }}
      >
        <StaggeredList config={SPRING_CONFIGS.quick} delay={1_500} tertiary>
          <Toolbar disableGutters>
            <Container
              disableGutters
              maxWidth="xl"
              sx={{
                marginTop: 1.5,
                paddingX: { lg: 2, xl: 0, xs: 2 },
                paddingY: 0,
                position: "relative",
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{
                  backdropFilter: trigger ? "blur(16px)" : "none",
                  background: trigger
                    ? "rgba(var(--mui-palette-background-defaultChannel) / 0.8)"
                    : "transparent",
                  border: trigger
                    ? "1px solid var(--mui-palette-divider)"
                    : "1px solid transparent",
                  borderRadius: 5,
                  boxShadow: trigger ? "var(--mui-shadows-2)" : "none",
                  height: "100%",
                  paddingX: 3,
                  paddingY: 1,
                  width: "100%",
                }}
              >
                <Grid container alignItems="center" width="100%">
                  <Grid size={{ md: 2, xs: 6 }}>
                    <Button
                      component={MuiLink}
                      href="/"
                      sx={{ borderRadius: 50, marginX: -2, paddingX: 2 }}
                    >
                      <Logo />
                    </Button>
                  </Grid>
                  <Grid size={{ md: 8, xs: 0 }} sx={{ display: { md: "block", xs: "none" } }}>
                    <Stack direction="row" justifyContent="center">
                      {navItems.map((item) => (
                        <NavButton key={item.label} {...item}>
                          {item.label}
                        </NavButton>
                      ))}
                    </Stack>
                  </Grid>
                  <Grid size={{ md: 2, xs: 6 }}>
                    <Stack direction="row" gap={2} alignItems="center" justifyContent="flex-end">
                      <ThemeMode sx={{ display: { sm: "inline-flex", xs: "none" } }} />
                      <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        edge="end"
                        onClick={handleDrawerToggle}
                        sx={{ display: { md: "none" } }}
                      >
                        <MenuIcon />
                      </IconButton>
                      <ShimmerButton target="_blank" href="https://1.privatefolio.app">
                        Live demo
                      </ShimmerButton>
                    </Stack>
                  </Grid>
                </Grid>
              </Stack>
            </Container>
          </Toolbar>
        </StaggeredList>
      </AppBar>
      <Box component="nav">
        <Drawer
          variant="temporary"
          anchor="left"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            "& .MuiDrawer-paper": {
              backdropFilter: "blur(32px)",
              background: "rgba(var(--mui-palette-background-defaultChannel) / 1)",
              borderLeft: "1px solid var(--mui-palette-divider)",
              boxSizing: "border-box",
              width: "80vw",
            },
            display: { md: "none", xs: "block" },
            "html.dark & .MuiDrawer-paper": {
              background: "rgba(var(--mui-palette-background-defaultChannel) / 0.25)",
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>
      <HeroSection />
    </>
  )
}
